<script setup lang="ts">
import { watch } from "vue";

import { useGetSitesQuery } from "@/composables/kili/sites/queries/use-get-sites-query";
import { useSearchFilter } from "@/composables/use-search-filter";
import { TSite } from "@/data/api/kili/sites/types/sites.type";
import { useTypedIntl } from "@/locales/typed-intl";
import router from "@/router/router";
import { ERouteName } from "@/router/router.type";

import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import Skeleton from "primevue/skeleton";

import BaseLayout from "@/components/layout/base-layout.vue";
import ProgrammingSiteCard from "@/components/programming/programming-site-card.vue";

const { t, k } = useTypedIntl();

const { data: sites, isLoading } = useGetSitesQuery();

const { searchInput: searchSiteInput, filteredList: filteredSites } = useSearchFilter<TSite>(sites, "name");

watch(
  sites,
  async (newValue) => {
    if (newValue && newValue.length === 1) {
      await router.push({ name: ERouteName.SITE_SECTIONS, params: { siteId: newValue[0].siteId } });
    }
  },
  { immediate: true },
);
</script>

<template>
  <BaseLayout
    :subtitle="t(k.tools.siteManagement.subtitle)"
    :title="t(k.tools.siteManagement.title)"
  >
    <div class="flex flex-col gap-4 pb-6">
      <div class="flex gap-4">
        <IconField>
          <InputIcon class="pi pi-search" />
          <InputText
            v-model.trim="searchSiteInput"
            data-test-id="section-management-search-site-input"
            :placeholder="t(k.global.searchSite)"
          />
        </IconField>
      </div>

      <h3 v-if="filteredSites && !isLoading">
        {{ t(k.global.count.sites({ count: filteredSites.length })) }}
      </h3>

      <Skeleton
        v-else
        class="!w-40 !h-8"
      />
    </div>

    <div class="flex flex-1 overflow-auto">
      <div class="flex flex-col gap-4 w-full pb-8 h-fit">
        <template v-if="filteredSites && !isLoading">
          <ProgrammingSiteCard
            v-for="site in filteredSites"
            :key="site.siteId"
            :site="site"
            @select="router.push({ name: ERouteName.SITE_SECTIONS, params: { siteId: $event } })"
          />
        </template>

        <template v-else>
          <Skeleton
            v-for="i in 3"
            :key="i"
            class="!h-24"
          />
        </template>
      </div>
    </div>
  </BaseLayout>
</template>
